// WebinarCertificatePage.jsx
// Same design as CertificatePage — bas "Months" ki jagah "Hours" hai

import { toPng } from "html-to-image";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { decryptParams } from "./encryptUrl";

import futureSkillsLogo from "./image/futureskills-img.png";
import googlePartnerLogo from "./image/google-partner-img.jpg";
import nasscomLogo from "./image/nasscom-img.png";
import skillIndiaLogo from "./image/skill-india-img.png";
import uncLogo from "./image/unc-img.png";
import goldAwardLogo from "./image/gold-award-img.png";
import gangeshSirSignature from "./image/gangeshSir.png";
import vijaySirSignature from "./image/vijaySir.png";
/* ── Date formatter: "19-06-2026" ya "2026-06-19" → "19th June 2026" ── */
function formatDate(str) {
  if (!str) return "";

  let day, month, year;

  // DD-MM-YYYY format (webinar CSV ka format)
  if (str.includes("-") && str.split("-")[0].length === 2) {
    [day, month, year] = str.split("-").map(Number);
  }
  // YYYY-MM-DD format
  else if (str.includes("-") && str.split("-")[0].length === 4) {
    [year, month, day] = str.split("-").map(Number);
  }
  else {
    return str; // already formatted
  }

  const monthNames = [
    "", "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const suffix =
    day % 10 === 1 && day !== 11 ? "st"
    : day % 10 === 2 && day !== 12 ? "nd"
    : day % 10 === 3 && day !== 13 ? "rd"
    : "th";

  return `${day}${suffix} ${monthNames[month]} ${year}`;
}

export default function WebinarCertificatePage() {
  const [params]  = useSearchParams();
  const certRef   = useRef(null);
  const qrRef     = useRef(null);
  const [loading, setLoading]   = useState(false);
  const [btnReady, setBtnReady] = useState(false);
  const [invalid, setInvalid]   = useState(false);

  /* ── Decrypt URL ── */
  const encryptedToken = params.get("d");

  let name, course, hours, email, rawDate, verifyId;

  if (encryptedToken) {
    const decoded = decryptParams(encryptedToken);
    if (!decoded) {
      name = course = hours = email = rawDate = verifyId = null;
    } else {
      name     = decoded.name           || "";
      course   = decoded.course         || "";
      hours    = decoded.hours          || decoded.months || ""; // hours field
      email    = decoded.email          || "";
      rawDate  = decoded.completionDate || decoded.date   || "";
      verifyId = decoded.verifyId       || "";
    }
  } else {
    // plain params fallback
    name     = params.get("name")           || "";
    course   = params.get("course")         || "";
    hours    = params.get("hours")          || params.get("months") || "";
    email    = params.get("email")          || "";
    rawDate  = params.get("completionDate") || params.get("date")   || "";
    verifyId = params.get("verifyId")       || "";
  }

  useEffect(() => {
    if (encryptedToken && !decryptParams(encryptedToken)) {
      setInvalid(true);
    }
  }, [encryptedToken]);

  const date = rawDate ? formatDate(rawDate) : "";

  // ── SIRF YAHI CHANGE HAI: "Hours Webinar" ──
  const courseText = hours && course
    ? `${hours} Hours Webinar on ${course}.`
    : course
    ? `Webinar on ${course}.`
    : "";

  // QR verify URL — webinar ke liye alag route
  const verifyUrl = verifyId
    ? `${window.location.origin}/webinar-verify/${verifyId}`
    : `${window.location.origin}`;

  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(verifyUrl)}`;

  /* show download button once QR loads */
  useEffect(() => {
    setBtnReady(false);
    const img = qrRef.current;
    if (!img) return;
    if (img.complete && img.naturalWidth > 0) { setBtnReady(true); return; }
    img.onload  = () => setBtnReady(true);
    img.onerror = () => setBtnReady(true);
  }, [qrSrc]);

  /* ── PNG Download ── */
  async function downloadPNG() {
    if (loading) return;
    try {
      setLoading(true);
      const dataUrl = await toPng(certRef.current, {
        cacheBust: true,
        pixelRatio: 3,
      });
      const link = document.createElement("a");
      link.download = `Webinar_Certificate_${(name || "Student").replace(/\s+/g, "_")}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error(err);
      alert("Download failed");
    } finally {
      setLoading(false);
    }
  }

  /* ── Invalid token screen ── */
  if (invalid) {
    return (
      <div style={{
        minHeight: "100vh", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        fontFamily: "Montserrat, sans-serif", background: "#f3f4f6",
      }}>
        <div style={{
          background: "#fff", borderRadius: 16, padding: "48px 40px",
          boxShadow: "0 4px 24px rgba(0,0,0,0.10)", textAlign: "center", maxWidth: 420,
        }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>🔒</div>
          <h2 style={{ color: "#1e2736", fontSize: 22, fontWeight: 800, margin: "0 0 10px" }}>
            Invalid Certificate Link
          </h2>
          <p style={{ color: "#64748b", fontSize: 15, lineHeight: 1.6 }}>
            Yeh link invalid ya expire ho gayi hai.<br />
            Apne admin se valid link maangein.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Great+Vibes&family=Montserrat:wght@400;500;600;700;800;900&display=swap');
        @page { size: landscape; margin: 0; }
        @media print {
          html, body { margin: 0 !important; padding: 0 !important; }
          .no-print { display: none !important; }
        }
        .cert-page-body {
          font-family: 'Montserrat', sans-serif;
          background-color: #e5e7eb;
          margin: 0;
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
          padding: 20px;
          flex-direction: column;
          gap: 20px;
        }
        .certificate-container {
          width: 1122px;
          height: 793px;
          background-color: #ffffff;
          position: relative;
          box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
          overflow: hidden;
        }
        .watermark {
          position: absolute;
          top: 38%; left: 50%;
          transform: translate(-50%, -50%);
          width: 455px; height: 460px;
          opacity: 0.4; z-index: 0;
          pointer-events: none;
          filter: grayscale(100%) brightness(1);
        }
        .cert-head { margin-top: -70px; font-size: 60px; font-weight: 700; font-family: 'Montserrat', sans-serif; }
        .of-trn    { margin-top: -10px; font-size: 30px; font-weight: 600; font-family: 'Montserrat', sans-serif; }
        .iso       { margin-top: 6px;   font-size: 24px; font-weight: 500; font-family: 'Montserrat', sans-serif; }
        .proudly   { margin-top: 8px;   font-size: 22px; font-weight: 500; font-family: 'Montserrat', sans-serif; }
        .unc-mainlogo { height: 240px; margin-top: -30px; margin-left: -85px; }
        .course-comp      { font-size: 18px; font-weight: bold; }
        .course-cert-date { font-size: 17px; font-weight: 600; }
        .founder-sign { margin-left: 20px; }
        .name-font { font-family: 'Great Vibes', cursive; }
        .dl-btn {
          background: #f05a28; color: white; border: none;
          padding: 14px 40px; border-radius: 10px; font-size: 15px;
          font-weight: 700; cursor: pointer; font-family: 'Montserrat', sans-serif;
          box-shadow: 0 4px 15px rgba(240,90,40,0.35);
          transition: all 0.2s; display: flex; align-items: center; gap: 8px;
        }
        .dl-btn:hover    { background: #d44a1e; transform: translateY(-1px); }
        .dl-btn:disabled { background: #aaa; cursor: not-allowed; transform: none; box-shadow: none; }
        .print-btn {
          background: #2d3542; color: white; border: none;
          padding: 14px 28px; border-radius: 10px; font-size: 15px;
          font-weight: 700; cursor: pointer; font-family: 'Montserrat', sans-serif;
          transition: all 0.2s;
        }
        .print-btn:hover { background: #1a2030; }
        .loader {
          border: 3px solid rgba(255,255,255,0.3);
          border-top: 3px solid white; border-radius: 50%;
          width: 18px; height: 18px;
          animation: spin 0.8s linear infinite; display: inline-block;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        @media print {
          .cert-page-body { background-color: white; padding: 0; margin: 0; justify-content: flex-start; align-items: flex-start; }
          .certificate-container { box-shadow: none; width: 1122px; height: 793px; page-break-after: avoid; }
          .no-print { display: none !important; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>

      <div className="cert-page-body">

        {/* Download Controls */}
        <div className="no-print" style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <button className="dl-btn" onClick={downloadPNG} disabled={loading || !btnReady}>
            {loading
              ? <><span className="loader" />&nbsp;Generating...</>
              : "⬇ Download Certificate (PNG)"
            }
          </button>
          <button className="print-btn" onClick={() => window.print()}>
            🖨 Print / PDF
          </button>
        </div>

        {/* ══════════════════════════════════════
            CERTIFICATE — bilkul same design
        ══════════════════════════════════════ */}
        <div className="certificate-container" ref={certRef}>

          {/* Watermark */}
          <img src={goldAwardLogo} className="watermark" alt="" />

          {/* Corner Top Right */}
          <svg className="absolute top-0 right-0"
            style={{ width: 420, height: 420, zIndex: 1 }}
            viewBox="0 0 100 100" preserveAspectRatio="none">
            <polygon points="100,0 100,20 80,0" fill="#f05a28" />
            <path d="M25 0 L75 0 L100 25 L110 51 Q97 30 56 0 Z" fill="#2d3542" />
          </svg>

          {/* Corner Bottom Left */}
          <svg className="absolute bottom-0 left-0"
            style={{ width: 420, height: 420, zIndex: 1 }}
            viewBox="0 0 100 100" preserveAspectRatio="none">
            <polygon points="0,100 0,80 20,100" fill="#f05a28" />
            <path d="M0 25 L0 75 L25 100 L64 110 Q30 97 0 61 Z" fill="#2d3542" />
          </svg>

          {/* Top Left Logo */}
          <div className="absolute z-20" style={{ top: 50, left: 60 }}>
            <div style={{ width: 240, height: 70, display: "flex", alignItems: "center", justifyContent: "flex-start", padding: 4 }}>
              <img src={uncLogo} className="unc-mainlogo" alt="Main Logo"
                onError={(e) => (e.target.style.display = "none")} />
            </div>
          </div>

          {/* Main Center Content */}
          <div className="absolute w-full flex flex-col items-center" style={{ top: 75, zIndex: 10 }}>

            <h1 className="cert-head">CERTIFICATE</h1>

            {/* ── SIRF YEH LINE CHANGE HUI: "OF PARTICIPATION" ── */}
            <h2 className="of-trn">OF PARTICIPATION</h2>

            <p className="iso">( ISO 9001 : 2016 Certified )</p>
            <p className="proudly">PROUDLY PRESENTED TO</p>

            {/* Student Name */}
            <div className="mt-16 mb-2 inline-block text-center pb-1"
              style={{ maxWidth: 900, borderBottom: "1px solid #d1d5db" }}>
              <div className="name-font text-center w-full"
                style={{ fontSize: 85, color: "#2d3542", lineHeight: 0.5 }}>
                {name}
              </div>
            </div>

            {/* Course — Hours wala text */}
            <div className="mt-6 text-center" style={{ color: "#1e2736" }}>
              <p style={{ fontSize: 23, fontWeight: 600, letterSpacing: "0.05em", margin: 0 }}>
                This is to certify that You have successfully participated in
              </p>
              <p style={{ fontSize: 23, fontWeight: 600, letterSpacing: "0.05em", margin: 0 }}>
                {/* ── SIRF YAHI CHANGE: hours + "Hours Webinar on" ── */}
                {courseText}
              </p>
            </div>

            {/* Date */}
            <div className="flex items-center justify-center mt-10" style={{ gap: 32 }}>
              <span className="course-comp">Webinar Date :</span>
              <span className="course-cert-date"
                style={{ borderBottom: "3px solid #2d3542", paddingLeft: 24, paddingRight: 24 }}>
                {date}
              </span>
            </div>
          </div>

          {/* Signatures & QR — same as original */}
          <div className="absolute w-full flex items-end"
            style={{
              bottom: 140, paddingLeft: 160, paddingRight: 160,
              justifyContent: "space-evenly", gap: 80, zIndex: 20, boxSizing: "border-box",
            }}>

            {/* Left — Founder */}
           <div className="text-center" style={{ width: 240 }}>
                     <div className="founder-sign">
                       <img
                         src={gangeshSirSignature}
                         style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
                         alt="gangesh sir "
                       />
                     </div>
                     <div>
                       <p style={{ fontWeight: 800, fontSize: 15, color: "#1e2736", letterSpacing: "0.05em", margin: "4px 0 0" }}>
                         Mr. Gangesh Kumar Gaunr
                       </p>
                       <p style={{ fontSize: 14, fontWeight: 700, color: "#374151", margin: "4px 0 0" }}>
                         Founder <br /> Uncodemy Edutech Pvt. Ltd.
                       </p>
                     </div>
                   </div>

            {/* QR Code */}
            <div className="flex flex-col items-center">
              <div style={{
                width: 110, height: 110, background: "#fff",
                border: "2px solid #f3f4f6",
                display: "flex", alignItems: "center", justifyContent: "center", padding: 2,
              }}>
                <img ref={qrRef} src={qrSrc} crossOrigin="anonymous"
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  alt="QR Code" />
              </div>
            </div>

         {/* Right Signature — CEO */}
                 <div className="text-center" style={{ width: 240 }}>
                 <div style={{
       height: 120,
       display: "flex",
       justifyContent: "center",
       alignItems: "flex-end",
       marginBottom: "-28px"
     }}>
       <img
         src={vijaySirSignature}
         style={{
           width: "69%",
           height: "auto",
           objectFit: "contain"
         }}
         alt="vijay Sir"
       />
     </div>
                   <div style={{ paddingTop: 8 }}>
                     <p style={{ fontWeight: 800, fontSize: 15, color: "#1e2736", letterSpacing: "0.05em", margin: "4px 0 0" }}>
                       Dr. Vijay Kumar Gupta
                     </p>
                     <p style={{ fontSize: 14, fontWeight: 700, color: "#374151", margin: "4px 0 0" }}>
                       Managing Director &amp; CEO,<br /> Uncodemy Edutech Pvt. Ltd.
                     </p>
                   </div>
                 </div>
          </div>

          {/* Footer Logos — same */}
          <div className="absolute w-full flex justify-center items-center"
            style={{ bottom: 40, gap: 4, marginLeft: 48, zIndex: 20 }}>
            <span style={{ fontWeight: 700, fontSize: 22, color: "#1e2736", marginRight: 4 }}>
              Certified By :
            </span>
            <div style={{ height: 60, width: 190, display: "flex", alignItems: "center", justifyContent: "center", padding: 4 }}>
              <img src={skillIndiaLogo} style={{ height: 170, width: 190 }} alt="Skill India"
                onError={(e) => (e.target.style.display = "none")} />
            </div>
            <div style={{ height: 60, width: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 4 }}>
              <img src={nasscomLogo} style={{ height: 75, width: 200 }} alt="NASSCOM"
                onError={(e) => (e.target.style.display = "none")} />
            </div>
            <div style={{ height: 60, width: 190, display: "flex", alignItems: "center", justifyContent: "center", padding: "4px 4px 4px 28px" }}>
              <img src={googlePartnerLogo} style={{ height: 60, width: 190 }} alt="Google Partner"
                onError={(e) => (e.target.style.display = "none")} />
            </div>
            <div style={{ height: 60, width: 190, display: "flex", alignItems: "center", justifyContent: "center", padding: 4 }}>
              <img src={futureSkillsLogo} style={{ height: 200, width: 190 }} alt="Future Skills"
                onError={(e) => (e.target.style.display = "none")} />
            </div>
          </div>

        </div>
      </div>
    </>
  );
}